import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressBar } from 'primereact/progressbar';
import { ScrollPanel } from 'primereact/scrollpanel';
import { apiService } from '../services/api';
import { WorkflowExecution } from '../types';
import WorkflowProgress from './WorkflowProgress';

interface WorkflowHistoryProps {
  projectId: string;
  onWorkflowSelect?: (executionArn: string) => void;
  currentWorkflow?: WorkflowExecution | null;
  onRefreshWorkflow?: () => void;
  workflowLoading?: boolean;
  onPauseWorkflow?: () => Promise<boolean>;
  onResumeWorkflow?: () => Promise<boolean>;
  onCancelWorkflow?: () => Promise<boolean>;
  onRetryWorkflow?: () => Promise<string | null>;
  onContactSupport?: (errorDetails: string) => void;
}

interface WorkflowHistoryItem {
  execution_arn: string;
  execution_name: string;
  status: string;
  started_at: string;
  ended_at?: string;
  duration?: number;
  current_step?: string;
  error?: string;
}

const WorkflowHistory: React.FC<WorkflowHistoryProps> = ({
  projectId,
  onWorkflowSelect,
  currentWorkflow,
  onRefreshWorkflow,
  workflowLoading = false,
  onPauseWorkflow,
  onResumeWorkflow,
  onCancelWorkflow,
  onRetryWorkflow,
  onContactSupport
}) => {
  const [workflows, setWorkflows] = useState<WorkflowHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowExecution | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | number[]>(0); // For accordion
  const activeWorkflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadWorkflowHistory();
  }, [projectId]);

  // Auto-scroll to active workflow when it starts
  useEffect(() => {
    if (currentWorkflow && currentWorkflow.status === 'RUNNING' && activeWorkflowRef.current) {
      activeWorkflowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentWorkflow?.status]);

  // Auto-expand active workflow section when workflow is running
  useEffect(() => {
    if (currentWorkflow && currentWorkflow.status === 'RUNNING') {
      setActiveIndex(0); // Expand the first accordion tab (active workflow)
    } else if (currentWorkflow && ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(currentWorkflow.status)) {
      // Auto-collapse when workflow completes
      setActiveIndex(-1);
    }
  }, [currentWorkflow?.status]);

  const loadWorkflowHistory = async () => {
    try {
      setLoading(true);
      const response = await apiService.listProjectWorkflows(projectId);
      
      if (response.success && response.data) {
        setWorkflows(response.data.workflows || []);
      }
    } catch (error) {
      console.error('Failed to load workflow history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowDetails = async (executionArn: string) => {
    try {
      setLoadingDetails(true);
      const response = await apiService.getWorkflowStatus(projectId, executionArn);
      
      if (response.success && response.data) {
        const workflowData: WorkflowExecution = {
          execution_arn: executionArn,
          execution_name: '',
          workflow_id: '',
          status: response.data.status as WorkflowExecution['status'],
          started_at: response.data.started_at || new Date().toISOString(),
          ended_at: response.data.ended_at,
          current_step: response.data.current_step,
          output: response.data.output,
          error: response.data.error,
          cause: response.data.cause
        };
        
        setSelectedWorkflow(workflowData);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Failed to load workflow details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return 'danger';
      case 'RUNNING':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'RUNNING':
        return 'Running';
      case 'SUCCEEDED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'TIMED_OUT':
        return 'Timed Out';
      case 'ABORTED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const formatDuration = (startedAt: string, endedAt?: string | null): string => {
    // Validate input dates
    if (!startedAt) return 'N/A';
    
    // Parse dates - handle both ISO strings and timestamps
    let start: number;
    let end: number;
    
    try {
      // Try parsing as ISO string first
      start = new Date(startedAt).getTime();
      
      // If startedAt is a number string (timestamp), parse it directly
      if (!isNaN(Number(startedAt)) && startedAt.length > 10) {
        start = Number(startedAt);
      }
      
      // If endedAt is null or undefined, use current time (for running workflows)
      if (endedAt === null || endedAt === undefined) {
        end = Date.now();
      } else {
        end = new Date(endedAt).getTime();
        // If endedAt is a number string (timestamp), parse it directly
        if (!isNaN(Number(endedAt)) && endedAt.length > 10) {
          end = Number(endedAt);
        }
      }
    } catch (error) {
      console.error('Date parsing error:', { startedAt, endedAt, error });
      return 'N/A';
    }
    
    // Check for invalid dates
    if (isNaN(start)) {
      console.warn('Invalid start date:', startedAt);
      return 'Invalid date';
    }
    if (isNaN(end)) {
      console.warn('Invalid end date:', endedAt);
      return 'Invalid date';
    }
    
    // Calculate duration in seconds
    const duration = Math.floor((end - start) / 1000);
    
    // Debug log for negative durations
    if (duration < 0) {
      console.warn('Negative duration detected:', {
        startedAt,
        endedAt,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        duration,
        message: 'This usually means the start time is in the future or there is a timezone/clock skew issue'
      });
      // Return absolute value with warning indicator
      const absDuration = Math.abs(duration);
      if (absDuration < 60) return `~${absDuration}s`;
      if (absDuration < 3600) {
        const minutes = Math.floor(absDuration / 60);
        const seconds = absDuration % 60;
        return `~${minutes}m ${seconds}s`;
      }
      const hours = Math.floor(absDuration / 3600);
      const minutes = Math.floor((absDuration % 3600) / 60);
      return `~${hours}h ${minutes}m`;
    }
    
    // Format duration nicely
    if (duration < 1) return '< 1s';
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}m ${seconds}s`;
    }
    
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getElapsedTime = (startedAt: string, endedAt?: string): string => {
    if (!startedAt) return 'N/A';
    
    const start = new Date(startedAt).getTime();
    if (isNaN(start)) return 'N/A';
    
    const end = endedAt ? new Date(endedAt).getTime() : Date.now();
    if (isNaN(end)) return 'N/A';
    
    const duration = Math.floor((end - start) / 1000);
    
    // Handle negative or invalid durations
    if (duration < 0) return 'N/A';
    if (duration < 1) return '< 1s';
    
    return formatDuration(startedAt, endedAt);
  };

  const getEstimatedTimeRemaining = (workflow: WorkflowExecution): string => {
    if (workflow.status !== 'RUNNING' || !workflow.current_step || !workflow.started_at) return '';
    
    // Estimate based on current step (simplified)
    const stepEstimates: { [key: string]: number } = {
      'ExtractIndicators': 120, // 2 minutes
      'DesignQuestionnaire': 180, // 3 minutes
      'DeployToKobo': 90 // 1.5 minutes
    };
    
    const remainingTime = stepEstimates[workflow.current_step] || 60;
    
    if (remainingTime < 60) return `~${remainingTime}s remaining`;
    if (remainingTime < 3600) return `~${Math.ceil(remainingTime / 60)}m remaining`;
    return `~${Math.ceil(remainingTime / 3600)}h remaining`;
  };

  const statusBodyTemplate = (rowData: WorkflowHistoryItem) => {
    return (
      <Tag
        value={getStatusLabel(rowData.status)}
        severity={getStatusSeverity(rowData.status)}
      />
    );
  };

  const dateBodyTemplate = (rowData: WorkflowHistoryItem) => {
    if (!rowData.started_at) return 'N/A';
    
    try {
      const date = new Date(rowData.started_at);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      // Format as: "Jan 2, 2026, 3:45 PM"
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const durationBodyTemplate = (rowData: WorkflowHistoryItem) => {
    // If backend provides duration, use it (it's in seconds)
    if (rowData.duration !== undefined && rowData.duration !== null) {
      const duration = Math.abs(rowData.duration); // Use absolute value just in case
      
      if (duration < 1) return '< 1s';
      if (duration < 60) return `${duration}s`;
      if (duration < 3600) {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}m ${seconds}s`;
      }
      
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
    
    // For running workflows (ended_at is null), show elapsed time
    if (!rowData.ended_at && rowData.status === 'RUNNING') {
      return formatDuration(rowData.started_at);
    }
    
    // For completed workflows, calculate from timestamps
    if (rowData.ended_at) {
      return formatDuration(rowData.started_at, rowData.ended_at);
    }
    
    // Fallback for other cases
    return 'N/A';
  };

  const actionsBodyTemplate = (rowData: WorkflowHistoryItem) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          icon="pi pi-eye"
          size="small"
          text
          tooltip="View Details"
          onClick={() => loadWorkflowDetails(rowData.execution_arn)}
          loading={loadingDetails}
        />
        {onWorkflowSelect && (
          <Button
            icon="pi pi-play"
            size="small"
            text
            tooltip="Monitor This Workflow"
            onClick={() => onWorkflowSelect(rowData.execution_arn)}
            disabled={rowData.status !== 'RUNNING'}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card title="Workflow History">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Skeleton width="100px" height="2rem" />
              <Skeleton width="150px" height="2rem" />
              <Skeleton width="100px" height="2rem" />
              <Skeleton width="80px" height="2rem" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* Active Workflow Section - Enhanced with Real-Time Monitoring */}
      {currentWorkflow && (
        <div ref={activeWorkflowRef}>
          <Card 
            title="🔄 Live Workflow Monitoring" 
            className="workflow-active-card" 
            style={{ marginBottom: '1.5rem' }}
          >
          <Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            <AccordionTab
              header={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  width: '100%',
                  padding: '0.5rem 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <i 
                      className={currentWorkflow.status === 'RUNNING' ? 'pi pi-spin pi-spinner' : 'pi pi-clock'} 
                      style={{ 
                        color: currentWorkflow.status === 'RUNNING' ? '#3B82F6' : 
                               currentWorkflow.status === 'SUCCEEDED' ? '#22C55E' : '#EF4444',
                        fontSize: '1.25rem'
                      }}
                    ></i>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1f2937' }}>
                        {currentWorkflow.status === 'RUNNING' ? 'Workflow In Progress' : 
                         currentWorkflow.status === 'SUCCEEDED' ? 'Workflow Completed' :
                         'Workflow Failed'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        {currentWorkflow.current_step && `Current: ${currentWorkflow.current_step}`}
                        {currentWorkflow.status === 'RUNNING' && ` • ${getElapsedTime(currentWorkflow.started_at)}`}
                        {currentWorkflow.status === 'RUNNING' && ` • ${getEstimatedTimeRemaining(currentWorkflow)}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {currentWorkflow.status === 'RUNNING' && (
                      <ProgressBar 
                        value={50} 
                        style={{ width: '100px', height: '6px' }}
                        showValue={false}
                      />
                    )}
                    <Tag
                      value={getStatusLabel(currentWorkflow.status)}
                      severity={getStatusSeverity(currentWorkflow.status)}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </div>
                </div>
              }
            >
              <WorkflowProgress
                workflow={currentWorkflow}
                onRefresh={onRefreshWorkflow}
                loading={workflowLoading}
                showHistory={true}
                onPause={onPauseWorkflow}
                onResume={onResumeWorkflow}
                onCancel={onCancelWorkflow}
                onRetry={onRetryWorkflow}
                onContactSupport={onContactSupport}
              />
              
              {/* Live Log Streaming Section */}
              {currentWorkflow.status === 'RUNNING' && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 600, 
                    color: '#374151',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <i className="pi pi-list" style={{ fontSize: '0.875rem' }}></i>
                    Live Execution Log
                  </h4>
                  <ScrollPanel style={{ 
                    width: '100%', 
                    height: '200px',
                    backgroundColor: '#1f2937',
                    borderRadius: '6px',
                    padding: '1rem'
                  }}>
                    <div style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.75rem',
                      color: '#e5e7eb',
                      lineHeight: 1.6
                    }}>
                      <div style={{ color: '#22c55e' }}>
                        [{new Date(currentWorkflow.started_at).toLocaleTimeString()}] Workflow started
                      </div>
                      {currentWorkflow.current_step && (
                        <div style={{ color: '#3b82f6' }}>
                          [{new Date().toLocaleTimeString()}] Executing step: {currentWorkflow.current_step}
                        </div>
                      )}
                      <div style={{ color: '#9ca3af' }}>
                        [{new Date().toLocaleTimeString()}] Processing... (elapsed: {getElapsedTime(currentWorkflow.started_at)})
                      </div>
                    </div>
                  </ScrollPanel>
                </div>
              )}
              
              {/* Quick Actions */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                {currentWorkflow.status === 'RUNNING' && (
                  <>
                    {onPauseWorkflow && (
                      <Button
                        label="Pause"
                        icon="pi pi-pause"
                        size="small"
                        text
                        onClick={onPauseWorkflow}
                      />
                    )}
                    {onCancelWorkflow && (
                      <Button
                        label="Cancel"
                        icon="pi pi-times"
                        size="small"
                        text
                        severity="danger"
                        onClick={onCancelWorkflow}
                      />
                    )}
                  </>
                )}
                {onRefreshWorkflow && (
                  <Button
                    label="Refresh Status"
                    icon="pi pi-refresh"
                    size="small"
                    text
                    onClick={onRefreshWorkflow}
                    loading={workflowLoading}
                  />
                )}
              </div>
            </AccordionTab>
          </Accordion>
        </Card>
        </div>
      )}

      <Card title="Workflow History" className="workflow-history-card">
        {workflows.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#6b7280'
          }}>
            <i className="pi pi-clock" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>No workflow executions found for this project.</p>
          </div>
        ) : (
          <DataTable
            value={workflows}
            paginator
            rows={10}
            className="p-datatable-sm"
            emptyMessage="No workflows found"
            sortField="started_at"
            sortOrder={-1}
          >
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
              style={{ width: '120px' }}
            />
            <Column
              field="started_at"
              header="Started"
              body={dateBodyTemplate}
              sortable
              style={{ width: '180px' }}
            />
            <Column
              field="duration"
              header="Duration"
              body={durationBodyTemplate}
              style={{ width: '100px' }}
            />
            <Column
              field="current_step"
              header="Last Step"
              sortable
              style={{ width: '150px' }}
            />
            <Column
              field="error"
              header="Error"
              body={(rowData) => rowData.error ? (
                <span style={{ color: '#dc2626', fontSize: '0.75rem' }}>
                  {rowData.error.length > 50 ? `${rowData.error.substring(0, 50)}...` : rowData.error}
                </span>
              ) : '-'}
              style={{ width: '200px' }}
            />
            <Column
              header="Actions"
              body={actionsBodyTemplate}
              style={{ width: '120px' }}
            />
          </DataTable>
        )}
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {workflows.length} workflow{workflows.length !== 1 ? 's' : ''} found
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            size="small"
            text
            onClick={loadWorkflowHistory}
            loading={loading}
          />
        </div>
      </Card>

      {/* Workflow Details Dialog - Keep for viewing historical workflows */}
      <Dialog
        header="Workflow Details"
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        style={{ width: '70vw', minWidth: '600px' }}
        modal
      >
        {selectedWorkflow && (
          <WorkflowProgress
            workflow={selectedWorkflow}
            showHistory={true}
            loading={loadingDetails}
          />
        )}
      </Dialog>
    </>
  );
};

export default WorkflowHistory;